import clsx from 'clsx'
import extractDomain from 'extract-domain'
import { PopOver, Tooltip } from '@pepper/ui'

import GoogleIntegrationButton from 'components/GoogleIntegrationButton'
import KeywordPill from 'container/Projects/LiveProjects/Project/IdeaLab/Topics/KeywordPill'
import GoogleIcon from 'assets/images/google_logo.svg'
import TopSearchResult from '../TopSearchResult'
import Header from '../Header'
import TopSearchLoader from '../TopSearchLoader'

import { getPrevMonthDuration } from 'utils/functions/IdeaLab/getPrevMonthDuration'

import { Topic, TopicKeyword } from '@pepper/utils/src/types/seo'
import { SuggestedTopic } from 'types/topic'
import {
  KeywordLabel,
  KeywordSelfRankingPage,
  KeywordTopRankingDetails,
} from 'types/keyword'

import s from './TopicDetailsPopover.module.css'

const RESULTS_COUNT = 3

type Props = {
  children: React.ReactNode
  primaryKeyword: string
  isLoading: boolean
  isGoogleConnected: boolean
  companyId: string
  topic: SuggestedTopic | Topic
  onGetKeywordLabel: () => void
  keywordLabel?: KeywordLabel
  topSearchResults?: KeywordTopRankingDetails[] | null
  selfRankingPages?: KeywordSelfRankingPage[] | null
  secondaryKeywords?: TopicKeyword[]
  domain?: string
  updatedAt?: string
  isDomainRemoved?: boolean
}

export const TopicDetailsPopover = ({
  children,
  primaryKeyword,
  isLoading,
  topSearchResults,
  selfRankingPages,
  keywordLabel,
  domain,
  companyId,
  onGetKeywordLabel,
  isGoogleConnected,
  secondaryKeywords,
  isDomainRemoved,
  updatedAt,
}: Props) => {
  const secondaryKeywordsToShow = secondaryKeywords?.slice(0, 5)

  const firstDay =
    !!updatedAt && getPrevMonthDuration(new Date(updatedAt)).firstDay
  const lastDay =
    !!updatedAt && getPrevMonthDuration(new Date(updatedAt)).lastDay

  return isDomainRemoved ? (
    <Tooltip
      title="Domain associated with this project has been removed"
      placement="top"
    >
      <div className={clsx(s.trigger, s.disabled)}>{children}</div>
    </Tooltip>
  ) : (
    <PopOver
      placement="bottom"
      overlayClassName={s.overlay}
      trigger="click"
      onVisibleChange={onGetKeywordLabel}
      content={
        <>
          <Header
            isLoading={isLoading}
            label={primaryKeyword}
            difficulty={keywordLabel?.difficulty}
            volume={keywordLabel?.volume}
          />
          <div className={s.results}>
            <div className={s.header}>
              <img src={GoogleIcon} width={16} alt="google-logo" />
              <div className={s.headerText}>Top Search Results</div>
            </div>
            <div className={s.list}>
              {isLoading && (
                <>
                  <TopSearchLoader />
                  <TopSearchLoader />
                  <TopSearchLoader />
                </>
              )}
              {topSearchResults &&
                !isLoading &&
                topSearchResults.slice(0, RESULTS_COUNT)?.map(result => (
                  <div key={result.url} className={s.result}>
                    <div className={s.positionIndicator}>
                      #{Math.round(result.position)}
                    </div>
                    <TopSearchResult
                      url={result.url}
                      displayUrl={result.displayed_url}
                      item={result}
                      logo={result.favicon}
                    />
                  </div>
                ))}
              {!topSearchResults && !isLoading && (
                <div className={s.noItems}>
                  <span className={s.positionIndicator}>--</span>
                  No ranking pages for this keyword
                </div>
              )}
            </div>
          </div>
          {isGoogleConnected ? (
            <div className={s.yourPages}>
              <div className={s.header}>
                Your Pages
                {firstDay && lastDay && (
                  <div className={s.duration}>
                    ({firstDay} - {lastDay})
                  </div>
                )}
              </div>
              <div className={s.list}>
                {isLoading && isGoogleConnected && (
                  <TopSearchLoader isSelfRankingPages={true} />
                )}
                {!isLoading &&
                  selfRankingPages &&
                  selfRankingPages.slice(0, RESULTS_COUNT)?.map(result => (
                    <div key={result.url} className={s.result}>
                      <div
                        className={clsx(
                          s.positionIndicator,
                          s.yourPagesPositionIndicator
                        )}
                      >
                        #{Math.round(result.position)}
                      </div>
                      <TopSearchResult
                        url={result.url}
                        displayUrl={result.url}
                        item={result}
                        logo={`https://logo.clearbit.com/${extractDomain(
                          domain ?? ''
                        )}`}
                        clicks={result.clicks}
                      />
                    </div>
                  ))}
                {!isLoading && !selfRankingPages && (
                  <div className={s.noItems}>
                    <span className={s.positionIndicator}>--</span>
                    No ranking pages for this keyword
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className={s.google}>
              <div className={s.googleLabel}>
                Connect account to see your ranking pages
              </div>
              <GoogleIntegrationButton
                type="ghost"
                companyId={companyId}
                size="middle"
              />
            </div>
          )}
          {!!secondaryKeywordsToShow?.length && (
            <div className={s.secondaryKeywords}>
              Secondary Keywords
              <div className={s.keywordsPill}>
                {secondaryKeywordsToShow.map(keywordItem => (
                  <KeywordPill
                    key={keywordItem.id}
                    keyword={keywordItem.keyword}
                    volume={keywordItem.volume}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      }
    >
      <button className={s.trigger} onClick={onGetKeywordLabel}>
        {children}
      </button>
    </PopOver>
  )
}
